/**
 * Dates, heures, montants et nombres tels que l'exploitant les lit.
 *
 * Une seule règle: tout est en français (`fr-FR`), jamais dans la langue du
 * navigateur, et jamais une date ISO brute à l'écran. Un `toLocaleString()`
 * sans locale affichait « 10/13/2026, 2:05:00 PM » sur un poste réglé en
 * anglais; ces fonctions rendent toujours « 13/10/2026 14:05 ».
 *
 * Aucune dépendance: ce module est importé par `libelles.js`, `erreurs.js` et
 * `utils.js`, il ne doit pas les importer en retour.
 *
 * Valeur vide ou illisible → `repli` (« — » par défaut), jamais « Invalid Date »
 * ni « null ».
 */

export const TIRET = '—';

const LOCALE = 'fr-FR';

/** `AAAA-MM-JJ` seul (une date de calendrier, sans heure). */
const DATE_SEULE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Début d'une date ISO, avec ou sans heure. */
const DATE_ISO = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Vrai si la chaîne est une date ISO (avec ou sans heure). */
export const estDateIso = (valeur) => typeof valeur === 'string' && DATE_ISO.test(valeur.trim());

/**
 * Convertit en `Date` ou renvoie `null`.
 *
 * Une date de calendrier « 2026-10-13 » est lue comme le 13 octobre local: la
 * lire comme minuit UTC l'afficherait la veille sur un poste à l'ouest de
 * Greenwich.
 */
export const enDate = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? null : valeur;
  if (typeof valeur === 'number') {
    const d = new Date(valeur);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof valeur !== 'string') return null;
  const texte = valeur.trim();
  const seule = DATE_SEULE.exec(texte);
  if (seule) {
    const d = new Date(Number(seule[1]), Number(seule[2]) - 1, Number(seule[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(texte);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 13/10/2026 */
export const formatDate = (valeur, repli = TIRET) => {
  const d = enDate(valeur);
  if (!d) return repli;
  return d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** 13/10/2026 14:05 */
export const formatDateHeure = (valeur, repli = TIRET) => {
  const d = enDate(valeur);
  if (!d) return repli;
  const jour = d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const heure = d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
  return `${jour} ${heure}`;
};

/** 14:05 */
export const formatHeure = (valeur, repli = TIRET) => {
  const d = enDate(valeur);
  if (!d) return repli;
  return d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
};

const ESPACE_INSECABLE = ' ';

/** 6 h 01: l'heure comme on la dit à voix haute (sans zéro devant l'heure). */
export const formatHeureParlee = (valeur, repli = TIRET) => {
  const d = enDate(valeur);
  if (!d) return repli;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${d.getHours()}${ESPACE_INSECABLE}h${ESPACE_INSECABLE}${minutes}`;
};

/**
 * Une durée en mots: « 40 s », « 12 min », « 3 h 05 », « 2 j ». Vide ou
 * illisible → `repli`; une durée négative (horloges décalées) vaut zéro.
 */
export const formatDuree = (millisecondes, repli = TIRET) => {
  if (typeof millisecondes !== 'number' || !Number.isFinite(millisecondes)) return repli;
  const secondes = Math.max(0, Math.floor(millisecondes / 1000));
  if (secondes < 60) return `${secondes} s`;
  const minutes = Math.floor(secondes / 60);
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) {
    const reste = minutes % 60;
    return reste ? `${heures} h ${String(reste).padStart(2, '0')}` : `${heures} h`;
  }
  return `${Math.floor(heures / 24)} j`;
};

/**
 * « il y a 40 s », « il y a 12 min », « il y a 3 h 05 », « il y a 2 j »; en
 * dessous de 5 secondes « à l'instant »; au-delà d'une semaine, la date
 * (« le 13/10/2026 »). Vide ou illisible → `repli`. `maintenant` (en
 * millisecondes) sert aux tests et aux écrans qui tiennent leur propre horloge.
 */
export const formatIlYA = (valeur, repli = TIRET, maintenant = Date.now()) => {
  const d = enDate(valeur);
  if (!d) return repli;
  const ecoule = maintenant - d.getTime();
  if (ecoule < 5000) return 'à l’instant';
  if (ecoule >= 7 * 86400000) return `le ${formatDate(d)}`;
  return `il y a ${formatDuree(ecoule)}`;
};

/** 13 octobre 2026 */
export const formatDateLongue =(valeur, repli = TIRET) => {
  const d = enDate(valeur);
  if (!d) return repli;
  return d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
};

/** octobre 2026, à partir de « 2026-10 », « 2026-10-13 » ou d'une date. */
export const formatMois = (valeur, repli = TIRET) => {
  if (typeof valeur === 'string' && /^\d{4}-\d{2}$/.test(valeur.trim())) {
    const [annee, mois] = valeur.trim().split('-').map(Number);
    return new Date(annee, mois - 1, 1).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
  }
  const d = enDate(valeur);
  if (!d) return repli;
  return d.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
};

/** Même chose que `formatDate`, mais le nom dit ce que l'écran verra si c'est vide. */
export const formatDateOuTiret = (valeur) => formatDate(valeur, TIRET);

/** Date + heure, ou « — ». */
export const formatDateHeureOuTiret = (valeur) => formatDateHeure(valeur, TIRET);

/** Nombre à la française: « 1 250 000 » (espace fine insécable). */
export const formatNombre = (valeur, decimales = 0, repli = TIRET) => {
  if (valeur === null || valeur === undefined || valeur === '') return repli;
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return repli;
  return nombre.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
};

/** Devises sans centimes: le serveur arrondit leurs montants au franc entier. */
const DEVISES_ENTIERES = new Set(['XOF', 'XAF', 'CFA', 'FCFA', 'JPY']);

/** Décimales d'un montant dans une devise: 0 pour le franc CFA, 2 sinon. */
export const decimalesDevise = (devise = 'XOF') =>
  DEVISES_ENTIERES.has(String(devise || 'XOF').trim().toUpperCase()) ? 0 : 2;

/**
 * Arrondi d'un montant comme le serveur (`core/money.py`): au quantum de la
 * devise, la moitié s'éloignant de zéro (2,5 → 3 ; −2,5 → −3).
 *
 * `Math.round(-2.5)` vaut −2 et `1.005 * 100` vaut 100,49999…: chaque étape
 * repasse par 15 chiffres significatifs pour effacer le bruit binaire avant
 * de décider de la moitié.
 */
export const arrondiDevise = (valeur, devise = 'XOF') => arrondiDecimales(valeur, decimalesDevise(devise));

/** Arrondi à `decimales` chiffres, la moitié s'éloignant de zéro. */
export const arrondiDecimales = (valeur, decimales = 0) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return 0;
  const facteur = 10 ** decimales;
  const net = (x) => Number(x.toPrecision(15));
  const absolu = net(Math.abs(net(nombre)) * facteur);
  const arrondi = Math.floor(absolu + 0.5) / facteur;
  return nombre < 0 && arrondi !== 0 ? -arrondi : arrondi;
};

/**
 * Totaux d'une facture calculés exactement comme le serveur
 * (`core/money.py::invoice_totals`): chaque ligne arrondie à la devise, puis
 * la somme des lignes, puis la TVA arrondie, puis le total.
 * Sans cela l'aperçu du formulaire annonçait un net à payer que la facture
 * enregistrée ne portait pas.
 */
export const totauxFacture = (lignes, tauxTva, devise = 'XOF') => {
  const totaux = (lignes || []).map((ligne) =>
    arrondiDevise(Number(ligne.quantity || 0) * Number(ligne.unit_price || 0), devise));
  const facteur = 10 ** decimalesDevise(devise);
  // Somme en unités entières du quantum: aucune addition binaire (0,1 + 0,2).
  const sousTotal = totaux.reduce((somme, total) => somme + Math.round(total * facteur), 0) / facteur;
  const tva = arrondiDevise(sousTotal * Number(tauxTva || 0), devise);
  const total = (Math.round(sousTotal * facteur) + Math.round(tva * facteur)) / facteur;
  return { totauxLignes: totaux, subtotal: sousTotal, tax_amount: tva, total_amount: total };
};

/** Prix unitaire avec ses décimales utiles: 735,5 le litre reste 735,5. */
export const formatPrixUnitaire = (montant, devise = 'XOF', repli = TIRET) => {
  if (montant === null || montant === undefined || montant === '') return repli;
  const nombre = Number(montant);
  if (!Number.isFinite(nombre)) return repli;
  const texte = nombre.toLocaleString(LOCALE, {
    minimumFractionDigits: decimalesDevise(devise),
    maximumFractionDigits: 6,
  });
  return `${texte} ${symboleDevise(devise)}`;
};

/** Symbole ou nom court affiché pour un code de devise. XOF et XAF → « FCFA ». */
const DEVISES = {
  XOF: 'FCFA',
  XAF: 'FCFA',
  CFA: 'FCFA',
  FCFA: 'FCFA',
  EUR: '€',
  USD: '$',
};

export const symboleDevise = (code, repli = 'FCFA') => {
  if (code === null || code === undefined || code === '') return repli;
  const cle = String(code).trim().toUpperCase();
  return DEVISES[cle] || cle;
};

/** 1 250 000 FCFA — la devise XOF n'apparaît jamais à l'écran. */
export const formatMontant = (montant, devise = 'XOF', decimales, repli = TIRET) => {
  // Par défaut, les décimales de la devise: 0 pour le franc CFA, 2 pour
  // l'euro — 10,50 € s'affichait « 11 € ». Montant arrondi comme le serveur.
  const places = decimales ?? decimalesDevise(devise);
  const valeur = montant === null || montant === undefined || montant === ''
    ? montant
    : arrondiDecimales(montant, places);
  const nombre = formatNombre(Number.isFinite(Number(montant)) ? valeur : montant, places, null);
  if (nombre === null) return repli;
  return `${nombre} ${symboleDevise(devise)}`;
};

/** 12 000 L */
export const formatLitres = (litres, decimales = 0, repli = TIRET) => {
  const nombre = formatNombre(litres, decimales, null);
  return nombre === null ? repli : `${nombre} L`;
};

/** 32 Ko, 1,5 Mo, 2,3 Go : l'unité suit la taille, jamais deux unités à la fois. */
export const formatTaille = (octets, repli = TIRET) => {
  const valeur = Number(octets);
  if (octets === null || octets === undefined || octets === '' || !Number.isFinite(valeur) || valeur < 0) return repli;
  if (valeur < 1024) return `${formatNombre(valeur, 0)} o`;
  if (valeur < 1024 ** 2) return `${formatNombre(valeur / 1024, 0)} Ko`;
  if (valeur < 1024 ** 3) return `${formatNombre(valeur / 1024 ** 2, 1)} Mo`;
  return `${formatNombre(valeur / 1024 ** 3, 1)} Go`;
};

/** 12,5 % */
export const formatPourcent = (valeur, decimales = 1, repli = TIRET) => {
  const nombre = formatNombre(valeur, decimales, null);
  return nombre === null ? repli : `${nombre} %`;
};

/**
 * « 1 alerte ouverte », « 3 alertes ouvertes »: l'accord se fait, au lieu
 * d'afficher « alerte(s) ouverte(s) ». `pluriel` vaut par défaut le singulier
 * suivi d'un s.
 */
export const accorde = (nombre, singulier, pluriel) => {
  const n = Number(nombre) || 0;
  const forme = Math.abs(n) >= 2 ? pluriel || `${singulier}s` : singulier;
  return `${n.toLocaleString('fr-FR')} ${forme}`;
};

/**
 * Remplace les dates ISO et le code de devise XOF d'une phrase libre (message
 * serveur, commentaire) par leur forme française. Le texte sans date passe
 * tel quel.
 */
export const francisaTexte = (texte) => {
  if (typeof texte !== 'string' || !texte) return texte;
  return texte
    .replace(
      /\b\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g,
      (iso) => (iso.length > 10 ? formatDateHeure(iso, iso) : formatDate(iso, iso)),
    )
    .replace(/\bXOF\b/g, 'FCFA');
};
